import React, { Fragment } from 'react';
import { Link } from '@tanstack/react-router';

import { formatDate } from '../../../helpers';

import type WaCheckbox from '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import type { Asset } from '../../../types/asset';

interface DashboardAdminAssetProps {
  asset: Asset;
  selected: boolean;
  handleSelectedChange: (asset: Asset, selected: boolean) => void;
  handleToggleHidden: (asset: Asset) => Promise<void>;
  handleEditAsset: (asset: Asset) => Promise<void>;
  handleDeleteAsset: (asset: Asset) => Promise<void>;
};

const DashboardAdminAsset: React.FC<DashboardAdminAssetProps> = ({
  asset,
  selected,
  handleSelectedChange,
  handleToggleHidden,
  handleEditAsset,
  handleDeleteAsset,
}) => {
  const isHidden = Boolean(asset.metadata?.hidden);
  const assetName = asset.title || asset.original_filename;

  return (
    <tr className={`dashboard-admin-asset${selected ? ' selected' : ''}`}>
      <td className="select">
        <wa-checkbox
          className="wa-visually-hidden-label"
          checked={selected}
          onChange={(event) => handleSelectedChange(asset, (event.target as WaCheckbox).checked)}
        >
          Select image {asset.title || asset.original_filename}
        </wa-checkbox>
      </td>
      <td className="thumbnail">
        <img src={asset.thumbnail} alt={asset.title} />
      </td>
      <td>{asset.title}</td>
      <td>
        {asset.groups?.map((group, index) => (
          <Fragment key={group.id}>
            {index > 0 && ', '}
            <span>{group.name}</span>
            {(group.visible_from || group.visible_to) && (
              <>
                <i className="fa-solid fa-info-circle" id={`group-${group.id}-name`} />
                <wa-tooltip for={`group-${group.id}-name`}>
                  Visible
                  {group.visible_from && <span> from {formatDate(group.visible_from)}</span>}
                  {group.visible_to && <span> to {formatDate(group.visible_to)}</span>}
                </wa-tooltip>
              </>
            )}
          </Fragment>
        ))}
      </td>
      <td>{asset.metadata?.hidden ? 'Yes' : 'No'}</td>
      <td>{asset.memories_count}</td>
      <td className="actions">
        <div className="wa-cluster wa-gap-2xs">
          <Link to={isHidden ? '#unhide' : '#hide'} id={`asset-${asset.id}-visibility`} aria-label={`${isHidden ? 'Unhide' : 'Hide'} image ${assetName}`} onClick={(e) => { e.preventDefault(); handleToggleHidden(asset); }}>
            <i className={`fa-light ${isHidden ? 'fa-eye' : 'fa-eye-slash'}`} />
          </Link>
          <wa-tooltip for={`asset-${asset.id}-visibility`} placement="top">
            {isHidden ? 'Unhide asset' : 'Hide asset'}
          </wa-tooltip>
          <Link to="#edit" id={`asset-${asset.id}-edit`} aria-label={`Edit image ${asset.title || asset.original_filename}`} onClick={(e) => { e.preventDefault(); handleEditAsset(asset); }}>
            <i className="fa-light fa-edit" />
          </Link>
          <wa-tooltip for={`asset-${asset.id}-edit`} placement="top">
            Edit asset
          </wa-tooltip>
          <Link to="#delete" id={`asset-${asset.id}-delete`} aria-label={`Delete image ${asset.title || asset.original_filename}`} onClick={(e) => { e.preventDefault(); handleDeleteAsset(asset); }}>
            <i className="fa-light fa-trash" />
          </Link>
          <wa-tooltip for={`asset-${asset.id}-delete`} placement="top">
            Delete asset
          </wa-tooltip>
        </div>
      </td>
    </tr>
  );
}

export default DashboardAdminAsset;
